package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * M4 A2 Stage 1：suspend lambda 同步调用检查。
 *
 * 算法：
 * 1. 扫描每个 fun / lambda body，维护当前作用域内的 `localSuspendVars: Set<String>`：
 *    `var name = LambdaExpr(isSuspend = true)` 时把 `name` 纳入集合。
 * 2. 对每个 [ExprAst.CallExpr]，若 callee 是 [ExprAst.LocalRef] 且名落在 localSuspendVars，
 *    且该 CallExpr 不直接处于 `AwaitExpr.suspendCall` 上下文，则报 [DslError.E607_SUSPEND_LAMBDA_NOT_AWAITED]。
 * 3. 进入嵌套 lambda 时新作用域继承外层 var 集（kotlin 闭包语义）。
 *
 * 局限：
 * - 仅识别 LocalRef 形态的直接调用；通过参数传递、return 出去再调用、被赋值到字段等场景不识别。
 *   Stage 1 范围内可接受，复杂数据流分析留待后续阶段。
 */
class SuspendLambdaUsageChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        screen.funs.forEach { walkStmts(it.body, emptySet()) }
    }

    /** 扫描语句序列；新声明的 suspend 变量加入返回的新作用域。 */
    private fun walkStmts(stmts: List<StmtAst>, outer: Set<String>) {
        var scope = outer
        for (s in stmts) {
            scope = walkStmt(s, scope)
        }
    }

    private fun walkStmt(s: StmtAst, scope: Set<String>): Set<String> {
        return when (s) {
            is StmtAst.VarDeclStmt -> {
                walkExpr(s.initializer, scope, inAwaitSuspendCall = false)
                val init = s.initializer
                if (init is ExprAst.LambdaExpr && init.isSuspend) {
                    scope + s.name
                } else {
                    // 重新声明同名变量时若不再是 suspend lambda，需要从集合中移除
                    if (s.name in scope) scope - s.name else scope
                }
            }
            is StmtAst.AssignStmt -> {
                walkExpr(s.rhs, scope, inAwaitSuspendCall = false)
                scope
            }
            is StmtAst.ExprStmt -> {
                walkExpr(s.call, scope, inAwaitSuspendCall = false)
                scope
            }
            is StmtAst.IfStmt -> {
                walkExpr(s.cond, scope, inAwaitSuspendCall = false)
                walkStmts(s.thenBody, scope)
                walkStmts(s.elseBody, scope)
                scope
            }
            is StmtAst.WhileStmt -> {
                walkExpr(s.cond, scope, inAwaitSuspendCall = false)
                walkStmts(s.body, scope)
                scope
            }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable, scope, inAwaitSuspendCall = false)
                walkStmts(s.body, scope)
                scope
            }
            is StmtAst.WhenStmt -> {
                walkExpr(s.whenExpr, scope, inAwaitSuspendCall = false)
                scope
            }
            is StmtAst.ReturnStmt -> {
                s.value?.let { walkExpr(it, scope, inAwaitSuspendCall = false) }
                scope
            }
            is StmtAst.TryStmt -> {
                walkStmts(s.tryBody, scope)
                walkStmts(s.catchBody, scope)
                walkStmts(s.finallyBody, scope)
                scope
            }
            // batch 语句：递归扫描 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> {
                walkStmts(s.body, scope)
                scope
            }
            is StmtAst.FunDeclStmt -> {
                walkStmts(s.decl.body, scope)
                scope
            }
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> scope
        }
    }

    /**
     * @param inAwaitSuspendCall 当前表达式是否直接作为 AwaitExpr.suspendCall —— 是则该层 CallExpr 不触发 E607
     */
    private fun walkExpr(e: ExprAst, scope: Set<String>, inAwaitSuspendCall: Boolean) {
        when (e) {
            is ExprAst.CallExpr -> {
                if (!inAwaitSuspendCall) {
                    val callee = e.callee
                    if (callee is ExprAst.LocalRef && callee.name in scope) {
                        diag.error(
                            DslError.E607_SUSPEND_LAMBDA_NOT_AWAITED,
                            e.position,
                            callee.name,
                        )
                    }
                }
                walkExpr(e.callee, scope, inAwaitSuspendCall = false)
                e.args.forEach { walkExpr(it, scope, inAwaitSuspendCall = false) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { walkExpr(it, scope, inAwaitSuspendCall = false) }
                e.suspendCall?.let { walkExpr(it, scope, inAwaitSuspendCall = true) }
                e.tailExpr?.let { walkExpr(it, scope, inAwaitSuspendCall = false) }
            }
            is ExprAst.LambdaExpr -> walkStmts(e.body, scope)
            is ExprAst.BinaryOp -> {
                walkExpr(e.left, scope, false); walkExpr(e.right, scope, false)
            }
            is ExprAst.UnaryOp -> walkExpr(e.operand, scope, false)
            is ExprAst.MemberAccess -> walkExpr(e.target, scope, false)
            is ExprAst.SafeMemberAccess -> walkExpr(e.target, scope, false)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(e.target, scope, false)
            is ExprAst.MemberCall -> {
                walkExpr(e.target, scope, false)
                e.args.forEach { walkExpr(it, scope, false) }
                e.namedArgs.forEach { walkExpr(it.value, scope, false) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(e.target, scope, false)
                e.args.forEach { walkExpr(it, scope, false) }
            }
            is ExprAst.Elvis -> {
                walkExpr(e.left, scope, false); walkExpr(e.right, scope, false)
            }
            is ExprAst.Ternary -> {
                walkExpr(e.cond, scope, false)
                walkExpr(e.thenExpr, scope, false)
                walkExpr(e.elseExpr, scope, false)
            }
            is ExprAst.IfExpr -> {
                walkExpr(e.cond, scope, false)
                walkStmts(e.thenBody, scope)
                walkStmts(e.elseBody, scope)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) walkExpr(it.expr, scope, false)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExpr(it, scope, false) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExpr(it.value, scope, false) }
            is ExprAst.RangeExpr -> {
                walkExpr(e.start, scope, false); walkExpr(e.endValue, scope, false)
            }
            is ExprAst.IndexExpr -> {
                walkExpr(e.target, scope, false); walkExpr(e.index, scope, false)
            }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { walkExpr(it, scope, false) }
            is ExprAst.NetSubscribeCall -> walkExpr(e.handler, scope, false)
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExpr(it.value, scope, false) }
                e.callback?.let { walkExpr(it, scope, false) }
                e.timeoutMs?.let { walkExpr(it, scope, false) }
            }
            is ExprAst.WhenExpr -> {
                walkExpr(e.subject, scope, false)
                e.branches.forEach { br ->
                    br.values.forEach { walkExpr(it, scope, false) }
                    walkStmts(br.body, scope)
                }
                e.elseBody?.let { walkStmts(it, scope) }
            }
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr, is ExprAst.IsExpr -> Unit
        }
    }
}
